import { SerializedComponent } from "../../components/SerializedApp";
import {
    ArrayScreenFormat,
    PropertyDescription,
    getPrimitiveColumns,
    TableGlideType,
    Description,
    MutatingScreenKind,
} from "../description";
import {
    SummaryArrayContentDescription,
    SummaryArrayScreenHandler,
    SummaryArrayScreenDescription,
} from "./summary-array-screen";
import { ListItemFlags } from "./list-item-flags";
import { ColumnPropertyFlag, ColumnPropertyHandler, SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { PropertyTableGetter, ComponentBuilder, PropertyDescriptor, ComponentConfiguratorContext } from "../components";
import { getColumnProperty } from "../components/property-utils";
import { InlineListBuild, makeEmptyMessage } from "./array-content";
import { handleTruncationAndGrouping } from "./truncation-lib";
import { setColumnInRowForEditComponent } from "../builder-utils";

function makeCheckedFlagNamePropertyHandler<T extends Description>(
    getIndirectTable: PropertyTableGetter<T> | undefined
): ColumnPropertyHandler<T> {
    return new ColumnPropertyHandler(
        "checkedFlagName",
        "Check Value",
        [
            ColumnPropertyFlag.Required,
            ColumnPropertyFlag.Editable,
            ColumnPropertyFlag.DefaultCaption,
            ColumnPropertyFlag.EditedInApp,
        ],
        getIndirectTable,
        undefined,
        getPrimitiveColumns,
        "boolean",
        PropertySection.Data
    );
}

const allowWrappingPropertyHandler = new SwitchPropertyHandler(
    { allowWrapping: false },
    "Allow text wrapping",
    PropertySection.TextStyle
);

interface CheckListArrayContentDescription extends SummaryArrayContentDescription {
    // This is the legacy of a bug.  The `checkedFlagName` was previously
    // named `propertyName` which collides with the same property in
    // inline list.
    readonly propertyName?: PropertyDescription;
    readonly checkedFlagName: PropertyDescription;
    readonly allowWrapping?: PropertyDescription;
}

function getCheckedFlagName(desc: CheckListArrayContentDescription, insideInlineList: boolean): string | undefined {
    // FIXME: It's a hack to make this property handler without a `getIndirectTable`
    // here.  We're only using it to get the value in the description, for which
    // case the indirect table doesn't matter, but this is not a nice way to do it.
    let propertyName = makeCheckedFlagNamePropertyHandler(undefined).getColumnName(desc);
    // See comment on `propertyName` above.
    if (propertyName === undefined && !insideInlineList) {
        propertyName = getColumnProperty(desc.propertyName);
    }
    return propertyName;
}

export interface CheckListArrayScreenDescription
    extends SummaryArrayScreenDescription,
        CheckListArrayContentDescription {}

export class CheckListArrayScreenHandler extends SummaryArrayScreenHandler<
    CheckListArrayContentDescription,
    CheckListArrayScreenDescription
> {
    protected readonly supportsNonURLImages = false;
    protected readonly supportsEmojiImages = true;
    protected readonly supportsTruncateList = true;

    constructor() {
        super(ArrayScreenFormat.CheckList, "Checklist", "checklist", false);
    }

    protected get supportsGrouping(): boolean {
        return true;
    }

    protected getInterSummaryPropertyDescriptors<T extends CheckListArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean
    ): readonly PropertyDescriptor[] {
        return [makeCheckedFlagNamePropertyHandler(getPropertyTable)];
    }

    public getContentPropertyDescriptors<T extends CheckListArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean
    ): ReadonlyArray<PropertyDescriptor> {
        const descrs: PropertyDescriptor[] = [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms
            ),
        ];

        descrs.push(allowWrappingPropertyHandler);
        return descrs;
    }

    protected content(
        builder: ComponentBuilder,
        desc: CheckListArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent | undefined,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        _uniqueID: string,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = getCheckedFlagName(desc, inlineListBuild !== undefined);
        if (propertyName === undefined) return b.emptyUI;
        const booleanValue = b.convertToBoolean(b.getProperty(propertyName, b.getContext, table));

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            undefined,
            propertyName,
            booleanValue,
            b.constant(false),
            table,
            undefined,
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const allowWrappingValue = allowWrappingPropertyHandler.getSwitch(desc);

        const flags = allowWrappingValue ? ListItemFlags.WrapText : undefined;

        return handleTruncationAndGrouping(
            builder,
            desc,
            table,
            arrayData,
            inlineListBuild,
            caption,
            1,
            true,
            (listData, isGrouped, a) =>
                b.ui("CheckList", {
                    list: listData,
                    caption,
                    emptyMessage: makeEmptyMessage(b, searchActiveFlag, inlineListBuild),
                    isGrouped,
                    allowWrapping: b.constant(allowWrappingValue),
                    item: b.adaptiveListItem(
                        title,
                        subtitle,
                        undefined,
                        image,
                        undefined,
                        undefined,
                        onTap,
                        onDropImage,
                        undefined,
                        flags,
                        b.let(
                            lets,
                            b.ui("CheckboxAccessory", {
                                value: valueToModify,
                                onChange: action,
                                isEnabled,
                            })
                        ),
                        undefined,
                        itemID
                    ),
                    ...a,
                })
        );
    }
}
